import dayjs from 'dayjs';
import type { TenantOrder } from '@/mocks/orders';
import type { OrderItem, PosOrderPrefill } from '@/pages/POS/types';

/**
 * CCCD do BE trả về đã mã hoá (PDPD EG-264/EG-265) mang tiền tố `v1:`. Chuỗi đó
 * KHÔNG phải số CCCD — đổ vào form đơn mới là gửi ciphertext lên BE làm CCCD
 * thật. Đơn sao chép thì để trống cho thu ngân nhập lại.
 */
function isEncryptedCccd(value: unknown): boolean {
  return typeof value === 'string' && value.startsWith('v1:');
}

/** Bỏ qua chuỗi rỗng/undefined để `customerForm` không sinh key rác. */
function putIfPresent(target: Record<string, unknown>, key: string, value: unknown): void {
  if (value === undefined || value === null || value === '') return;
  target[key] = value;
}

/**
 * Đóng gói một đơn đã lưu thành dữ liệu mồi cho POS.
 *
 * Hàm THUẦN: chỉ dựng `customerForm` từ đơn. Dòng hàng KHÔNG dựng ở đây —
 * chúng phải đi qua `posService.lookupVariants` để có `variantCode`/`vat`/
 * `retailPrice` THẬT (xem `buildCopyOrderItems.ts`), nên caller tra trước rồi
 * truyền kết quả vào tham số `orderItems`.
 */
export function mapOrderToPosPrefill(
  order: TenantOrder,
  orderItems: OrderItem[],
): PosOrderPrefill {
  const customerForm: Record<string, unknown> = {};

  if (order.customer) {
    putIfPresent(customerForm, 'customer_name', order.customer.name);
    putIfPresent(customerForm, 'customer_phone', order.customer.phone);
    putIfPresent(customerForm, 'customer_email', order.customer.email);
  }

  // TCR-2460: id khách của đơn gốc — cùng field `Orders/edit.tsx` hydrate.
  // Gợi ý giá B2B (`applyB2BSuggestToOrderItems`) gửi id này làm `customer_id`;
  // thiếu nó BE không biết kênh/vùng của khách nên không khớp bảng giá riêng
  // (vd GT miền Bắc) và cũng không tính được cấp theo sản lượng ⇒ giá sai.
  putIfPresent(customerForm, 'customer_user_id', order.user_id);

  // Loại khách quyết định bộ field bắt buộc của sidebar (MST cho tổ chức) —
  // không chép thì đơn B2B sao chép ra lại thành đơn cá nhân.
  if (order.user_type === 'personal' || order.user_type === 'organization') {
    customerForm.user_type = order.user_type;
  }

  // TCR-2129: mang kênh B2B của đơn gốc sang POS để cột CK chính sách bật ngay.
  putIfPresent(customerForm, '_b2bChannel', order.b2b_channel);

  const address = order.shipping?.address;
  if (address) {
    putIfPresent(customerForm, 'customer_address', address.address);
    // `province_id` là mã đơn vị hành chính MỚI; đơn cũ chỉ có `city_id`.
    putIfPresent(customerForm, 'customer_city_id', address.province_id || address.city_id);
    putIfPresent(customerForm, 'customer_ward_id', address.ward_id);
    // Shadow field hiển thị-only đọc bởi `SelectedCustomerCard` (xem
    // `CustomerSidebar/index.tsx`) — thiếu chúng thì card vẫn hiện địa chỉ bị
    // cắt cụt dù id đã bind đúng. Cùng cặp field `Orders/edit.tsx` nạp ở
    // TCR-1571.
    putIfPresent(customerForm, '_cityName', address.city_name);
    putIfPresent(customerForm, '_wardName', address.ward_name);
    if (customerForm.customer_email === undefined) {
      putIfPresent(customerForm, 'customer_email', address.email);
    }
    if (address.customer_birthday) {
      // `DateMaskInput` (CustomerInfoFields) nhận chuỗi DD/MM/YYYY, không nhận Dayjs.
      customerForm.customer_birthday = dayjs(address.customer_birthday).format('DD/MM/YYYY');
    }
    if (!isEncryptedCccd(address.citizen_id)) {
      putIfPresent(customerForm, 'citizen_id', address.citizen_id);
    }

    // NGƯỜI NHẬN (TCR-1612) — `shipping.address.name/phone` là thông tin người
    // nhận đã lưu của đơn. Không nạp vào nhóm `delivery_*` thì tab THÔNG TIN
    // GIAO NHẬN của đơn sao chép ra RỖNG hoàn toàn, kể cả khi đơn gốc có đủ:
    // bộ field đó chỉ được điền bởi effect "Giống thông tin khách hàng" nằm
    // trong `DeliveryRecipientFields`, mà effect ấy chưa hề chạy trước lần mở
    // dialog đầu tiên. Cùng hợp đồng `Orders/edit.tsx` đã nạp cho màn Sửa đơn
    // — hai màn đọc CÙNG một bộ field nên không được lệch nhau.
    putIfPresent(customerForm, 'delivery_name', address.name);
    putIfPresent(customerForm, 'delivery_phone', address.phone);
    putIfPresent(customerForm, 'delivery_email', address.email);
    putIfPresent(customerForm, 'delivery_address', address.address);
    putIfPresent(customerForm, 'delivery_city_id', address.province_id || address.city_id);
    putIfPresent(customerForm, 'delivery_ward_id', address.ward_id);
    putIfPresent(customerForm, '_deliveryCityName', address.city_name);
    putIfPresent(customerForm, '_deliveryWardName', address.ward_name);
    putIfPresent(customerForm, 'delivery_note', address.note);
    if (!isEncryptedCccd(address.citizen_id)) {
      putIfPresent(customerForm, 'delivery_citizen_id', address.citizen_id);
    }
    // Người nhận KHÁC khách hàng (mua hộ / giao người thân) thì phải TẮT
    // "Giống thông tin khách hàng": để bật, effect đồng bộ ghi đè tên + SĐT
    // người nhận bằng thông tin khách ngay khi dialog mở lần đầu — mất đúng
    // dữ liệu vừa chép mà không có gì báo.
    customerForm.delivery_same_as_customer =
      (address.name ?? '') === (order.customer?.name ?? '') &&
      (address.phone ?? '') === (order.customer?.phone ?? '');
  }

  if (typeof order.note === 'string') {
    putIfPresent(customerForm, 'description', order.note);
  }

  if (order.buyer_invoice_info) {
    const invoice = order.buyer_invoice_info;
    // Phải TẮT "giống thông tin khách hàng", nếu không effect đồng bộ trong
    // `InvoiceSection` ghi đè sạch thông tin hoá đơn vừa chép.
    customerForm.invoice_same_as_delivery = false;
    putIfPresent(customerForm, 'invoice_name', invoice.name || invoice.buyer_name);
    putIfPresent(customerForm, 'invoice_tax_id', invoice.tax_id);
    putIfPresent(customerForm, 'invoice_address', invoice.address);
    putIfPresent(customerForm, 'invoice_email', invoice.email);
    putIfPresent(customerForm, 'buyer_name', invoice.buyer_name);
    if (!isEncryptedCccd(invoice.buyer_cccd)) {
      putIfPresent(customerForm, 'buyer_cccd', invoice.buyer_cccd);
    }
  }

  // CỐ Ý không chép: voucher/điểm thưởng/CKTM/phụ thu/phí ship của đơn cũ. Đó là
  // kết quả tính toán của MỘT lần bán (có hạn dùng, có suất dùng, đã trừ điểm) —
  // chép sang đơn mới là dựng sẵn số tiền sai mà không ai bấm gì để tạo ra nó.

  return { orderItems, customerForm };
}
